import _groupBy from 'lodash/groupBy';

import {nodeTypeConfig} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {CampaignTreeType} from 'common/src/modules/campaigns/campaigns.types';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import tabularDataToCsv from 'common/src/modules/shared/copilot/csvUtils/tabularDataToCsv';
import {IssueLevelToIssueName} from 'common/src/modules/issues/issues.config';
import {CLIssueLevel} from 'common/src/modules/issues/issues.types';
import {DisconnectedMapNode, NodeType} from 'common/src/modules/nodes/nodes.types';
import {CLStore} from 'common/src/redux/store';
import {campaignTypeNameMap} from '../../../campaigns.consts';
import getDestinationCardPhrase from './utils/nodeDetails/getDestinationCardPhrase';
import getNodeSummary from './utils/nodeDetails/getNodeSummary';

type CampaignSetupCard = {
  id: number;
  name: string;
  summary: string;
  type: NodeType;
  nextNodes: number[];
  issues: CampaignSetupCardIssue[];
};

type CampaignSetupCardIssue = {
  level: CLIssueLevel;
  message: string;
};

type CampaignSetupContext = {
  cards: CampaignSetupCard[];
};

const MAX_CARDS_TO_DISPLAY = 30;

function _getNodeOutputsSummary(node: DisconnectedMapNode): string {
  return node.outputs
    .map((output) => `${output.id} ${getDestinationCardPhrase(output.nodeId)}`)
    .join('\n');
}

function campaignSetupContext(store: CLStore): AgentContext<CampaignSetupContext> {
  function getter(): CampaignSetupContext {
    const state = store.getState();
    const issues = state.campaignPage.issues.campaignLogicIssues;
    const campaign = state.campaignPage.campaignTree.campaign;
    const campaignLogic = state.campaignPage.campaignLogic;
    const campaignNodes = campaignLogic.nodes[CampaignTreeType.Campaign];

    if (!campaign) {
      return {
        cards: [],
      };
    }

    const issuesByCardId = _groupBy(issues, 'nodeId');

    const cards: CampaignSetupCard[] = Object.values(campaignNodes).map(
      (node: DisconnectedMapNode) => {
        return {
          id: node.id,
          name:
            node.type === NodeType.CAMPAIGN
              ? 'Campaign Card'
              : node.name || nodeTypeConfig[node.type].name,
          summary:
            node.type === NodeType.CAMPAIGN
              ? `${campaignTypeNameMap[campaign.type]} campaign card`
              : getNodeSummary(node.id),
          outputs: _getNodeOutputsSummary(node),
          type: node.type,
          nextNodes: node.outputs?.map((output) => output.nodeId || 0).filter(Boolean) || [],
          issues:
            issuesByCardId[node.id]
              ?.map((issue) => ({
                level: issue.level,
                message: issue.message as string,
              }))
              .filter(Boolean) || [],
        };
      },
    );

    return {
      cards,
    };
  }

  function formatter({currentContext}: {currentContext: CampaignSetupContext}): string {
    if (!currentContext) {
      return '';
    }

    const {cards} = currentContext;

    if (cards.length === 0) {
      return 'No campaign cards are currently available for setup.';
    }

    const cardsForDisplay = cards.slice(0, MAX_CARDS_TO_DISPLAY);

    const cardSumamries = cardsForDisplay
      .map((card) =>
        card.type === NodeType.CAMPAIGN
          ? `Card ID: ${card.id} - Campaign Card`
          : getNodeSummary(card.id),
      )
      .join('\n\n');

    // Collect all issues from all cards in CSV format
    const issues = cardsForDisplay.flatMap((card) =>
      card.issues.map((issue) => ({
        cardId: card.id,
        cardName: card.name,
        issueLevel: IssueLevelToIssueName[issue.level],
        message: issue.message,
      })),
    );

    const issuesSummary = issues.length > 0 ? `${tabularDataToCsv(issues)}` : '';

    const cardsSummary =
      cards.length > MAX_CARDS_TO_DISPLAY
        ? `Campaign has ${cards.length} cards. Showing first ${MAX_CARDS_TO_DISPLAY}: \n\n ${cardSumamries}`
        : `Cards in the campaign:\n${cardSumamries}`;

    return `${cardsSummary}\n\nCard Issues: \n${issuesSummary}`;
  }

  return {
    title: 'Campaign Setup Cards',
    getter,
    formatter,
  };
}

export default campaignSetupContext;
